import React from "react";
import { Link } from "react-router-dom";
import { CheckSquare, Calendar, ListChecks } from "lucide-react";
import SEO from "../components/SEO";

export const ComplianceChecklistUK = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Compliance Checklist for Small Businesses (UK 2026)",
    "description": "Complete HMRC compliance checklist for UK small businesses. Monthly, quarterly, and annual tasks to stay compliant and avoid penalties.",
    "author": { "@type": "Organization", "name": "Brightson Accounting" },
    "publisher": { "@type": "Organization", "name": "Brightson Accounting" },
    "datePublished": "2026-04-06"
  };

  return (
    <>
      <SEO
        title="Compliance Checklist UK Small Business 2026 | Brightson Accounting"
        description="UK small business compliance checklist: monthly, quarterly, annual tasks. Registration, deadlines, record-keeping. Avoid penalties with expert Wolverhampton accountants."
        canonical="/blog/compliance-checklist-uk"
        schema={articleSchema}
      />

      <article className="blog-article">
        <header className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category"><CheckSquare size={16} /> Staying Compliant</span>
              <span className="article-date">6 April 2026</span>
              <span className="article-read-time">6 min read</span>
            </div>

            <h1>Compliance Checklist for Small Businesses (UK 2026)</h1>
            <p className="article-subtitle">
              Follow this simple checklist and you'll avoid 90% of HMRC compliance issues
            </p>

            <div className="article-author">
              <div className="author-info">
                <strong>Brightson Accounting (FCCA Qualified)</strong>
                <span>Compliance Specialists | Wolverhampton</span>
              </div>
            </div>
          </div>
        </header>

        <section className="article-content">
          <div className="container">
            <div className="article-body">
              
              <p className="article-lead">
                At Brightson Accounting, we help businesses across Wolverhampton and the West Midlands stay compliant with HMRC. If you follow this simple compliance checklist, you'll avoid most common issues.
              </p>

              <div className="info-box">
                <strong>Quick Summary</strong>
                <ul>
                  <li>Monthly: Reconcile bank accounts, chase invoices, update bookkeeping</li>
                  <li>Quarterly: Submit VAT returns, review profit/loss, plan tax payments</li>
                  <li>Annual: File Self Assessment, Corporation Tax, issue P60s, review tax strategy</li>
                  <li>Print this checklist and tick off each task as you complete it</li>
                </ul>
              </div>

              <h2>Registration Checklist (One-Time Setup)</h2>

              <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                <strong>✅ Before You Start Trading</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>Register as sole trader or incorporate limited company</li>
                  <li>Register for Self Assessment (if self-employed)</li>
                  <li>Open separate business bank account</li>
                  <li>Set up business insurance</li>
                  <li>Choose accounting software</li>
                </ul>
              </div>

              <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50', marginTop: '1rem' }}>
                <strong>✅ When You Reach Thresholds</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>Register for VAT when turnover exceeds £90,000</li>
                  <li>Register for PAYE when you hire first employee</li>
                  <li>Consider Making Tax Digital compliance (if income > £50k)</li>
                </ul>
              </div>

              <h2>Monthly Compliance Tasks</h2>

              <div className="info-box" style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
                <strong>📅 Every Month (Takes ~2 Hours)</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>Reconcile bank accounts</strong> — Compare bookkeeping to bank statements</li>
                  <li><strong>Chase unpaid invoices</strong> — Follow up on overdue payments</li>
                  <li><strong>Review expenses</strong> — Categorize and file receipts</li>
                  <li><strong>Update bookkeeping</strong> — Enter all transactions into software</li>
                  <li><strong>Review cash flow</strong> — Ensure sufficient funds for tax payments</li>
                  <li><strong>Pay PAYE</strong> — If you have employees (19th/22nd of month)</li>
                </ul>
              </div>

              <p>
                <strong>Why Monthly Matters:</strong> Leaving bookkeeping until year-end = stress, errors, and missed expense claims. Monthly updates keep everything accurate and manageable.
              </p>

              <p>Full guide: <Link to="/blog/bookkeeping-compliance-uk">Bookkeeping Basics for Compliance</Link></p>

              {/* Mid-Article CTA */}
              <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
                <h3>💡 Want to see how much tax you could save?</h3>
                <p>Most UK business owners are overpaying without realising it.</p>
                <Link to="/corporation-tax-calculator" className="article-cta-button">
                  👉 Try the Corporation Tax Calculator
                </Link>
              </div>

              <h2>Quarterly Compliance Tasks</h2>

              <div className="info-box" style={{ background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                <strong>📅 Every Quarter (Takes ~3 Hours)</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>Submit VAT return</strong> — If VAT registered (deadline: 1 month + 7 days after quarter)</li>
                  <li><strong>Submit MTD updates</strong> — If applicable (quarterly digital submissions)</li>
                  <li><strong>Review profit/loss</strong> — Are you on track for year-end targets?</li>
                  <li><strong>Plan tax payments</strong> — Estimate and save for upcoming tax bills</li>
                  <li><strong>Review pricing</strong> — Are your margins healthy?</li>
                  <li><strong>Check compliance</strong> — Any changes to HMRC rules affecting you?</li>
                </ul>
              </div>

              <p><strong>VAT Deadlines:</strong></p>
              <ul>
                <li>Quarter ending 31 March → Submit by 7 May</li>
                <li>Quarter ending 30 June → Submit by 7 August</li>
                <li>Quarter ending 30 September → Submit by 7 November</li>
                <li>Quarter ending 31 December → Submit by 7 February</li>
              </ul>

              <p>Full guide: <Link to="/blog/vat-compliance-mistakes">VAT Compliance: Common Mistakes</Link></p>

              <h2>Annual Compliance Tasks</h2>

              <div className="info-box" style={{ background: '#fce4ec', borderLeft: '4px solid #e91e63' }}>
                <strong>📅 Every Year (Or Work with Accountant)</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>File Self Assessment</strong> — Deadline: 31 January (if self-employed/director)</li>
                  <li><strong>Pay Self Assessment tax</strong> — Deadline: 31 January</li>
                  <li><strong>Second payment on account</strong> — Deadline: 31 July</li>
                  <li><strong>File Corporation Tax (CT600)</strong> — Deadline: 12 months after year-end</li>
                  <li><strong>Pay Corporation Tax</strong> — Deadline: 9 months + 1 day after year-end</li>
                  <li><strong>Issue P60s to employees</strong> — Deadline: 19 May</li>
                  <li><strong>Submit P11D forms</strong> — Deadline: 6 July (if benefits in kind)</li>
                  <li><strong>Review tax planning</strong> — Can you reduce next year's tax bill?</li>
                  <li><strong>Update business plan</strong> — Set targets for new financial year</li>
                </ul>
              </div>

              <p>Full guide: <Link to="/blog/hmrc-deadlines-uk">HMRC Deadlines You Cannot Afford to Miss</Link></p>

              <h2>Compliance by Business Type</h2>

              <h3>Sole Trader Checklist</h3>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Frequency</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Task</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Monthly</td>
                    <td style={{ padding: '0.75rem' }}>Update bookkeeping, reconcile bank</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Quarterly</td>
                    <td style={{ padding: '0.75rem' }}>VAT return (if registered), MTD updates</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>Annual</td>
                    <td style={{ padding: '0.75rem' }}>Self Assessment return (31 Jan), payments on account</td>
                  </tr>
                </tbody>
              </table>

              <h3>Limited Company Checklist</h3>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Frequency</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Task</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Monthly</td>
                    <td style={{ padding: '0.75rem' }}>PAYE payment, payroll submission, bookkeeping</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Quarterly</td>
                    <td style={{ padding: '0.75rem' }}>VAT return (if registered), board meetings</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>Annual</td>
                    <td style={{ padding: '0.75rem' }}>Corporation Tax (CT600), Self Assessment (directors), P60s, P11Ds, annual accounts, confirmation statement</td>
                  </tr>
                </tbody>
              </table>

              <p>Full guide: <Link to="/blog/corporation-tax-compliance">Corporation Tax Compliance Explained</Link></p>

              <h2>Software & Tools Recommendations</h2>

              <p>To make compliance easier, we recommend:</p>

              <h3>Accounting Software</h3>
              <ul>
                <li><strong>Xero</strong> — Comprehensive, MTD-compliant, cloud-based</li>
                <li><strong>QuickBooks</strong> — User-friendly, good for small businesses</li>
                <li><strong>FreeAgent</strong> — Designed for freelancers and contractors</li>
                <li><strong>Sage</strong> — Traditional option, widely used</li>
              </ul>

              <h3>Receipt Management</h3>
              <ul>
                <li><strong>Dext (Receipt Bank)</strong> — Photo receipts, auto-extract data</li>
                <li><strong>Expensify</strong> — Expense tracking and approval</li>
                <li><strong>AutoEntry</strong> — Invoice and receipt capture</li>
              </ul>

              <h3>Calendar & Reminders</h3>
              <ul>
                <li>Google Calendar with recurring deadlines</li>
                <li>Accounting software deadline alerts</li>
                <li>Work with accountant who tracks deadlines</li>
              </ul>

              <h2>How to Use This Checklist</h2>

              <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                <strong>✅ Implementation Steps</strong>
                <ol style={{ marginBottom: 0 }}>
                  <li>Print this checklist or save as PDF</li>
                  <li>Set calendar reminders for all monthly/quarterly/annual tasks</li>
                  <li>Block time in your calendar (monthly: 2 hours, quarterly: 3 hours)</li>
                  <li>Tick off each task as completed</li>
                  <li>Review checklist quarterly — any missed tasks?</li>
                  <li>Update checklist if your business structure changes</li>
                </ol>
              </div>

              <h2>When to Hire an Accountant</h2>

              <p>Consider working with an accountant if:</p>
              <ul>
                <li>You don't have time for monthly/quarterly tasks</li>
                <li>You're not confident with tax rules</li>
                <li>You want to optimize tax (not just stay compliant)</li>
                <li>Your turnover exceeds £50,000</li>
                <li>You have employees</li>
                <li>You're VAT registered</li>
                <li>You run a limited company</li>
              </ul>

              <p>
                At Brightson Accounting, we handle all compliance tasks for businesses across Wolverhampton and the West Midlands. You focus on running your business — we handle HMRC.
              </p>

              <h2>Final Thoughts</h2>

              <p>
                Compliance doesn't have to be complicated. If you follow this checklist consistently:
              </p>

              <ul>
                <li>You'll never miss a deadline</li>
                <li>Your records will always be accurate</li>
                <li>You'll claim all allowable expenses</li>
                <li>HMRC enquiries will be rare (and easy to handle)</li>
                <li>You'll sleep better knowing you're compliant</li>
              </ul>

              <p>
                Want to stay compliant and <Link to="/corporation-tax-calculator">reduce your corporation tax</Link> at the same time? <Link to="/contact">Speak to an accountant</Link> today.
              </p>

            </div>

            {/* End CTA */}
            <div className="article-cta-box" style={{ marginTop: '3rem' }}>
              <h2>🚀 Ready to stay compliant and reduce your tax bill?</h2>
              <p>We help businesses across Wolverhampton and the West Midlands:</p>
              <ul>
                <li>Stay compliant with HMRC</li>
                <li>Reduce tax legally</li>
                <li>Avoid penalties and investigations</li>
              </ul>
              <Link to="/contact" className="article-cta-button">👉 Book a Free Consultation</Link>
            </div>

            <div className="related-articles" style={{ marginTop: '3rem' }}>
              <h3>Related Guides</h3>
              <div className="related-links">
                <Link to="/blog/hmrc-compliance-guide-uk" className="related-link">HMRC Compliance Guide</Link>
                <Link to="/blog/hmrc-deadlines-uk" className="related-link">HMRC Deadlines</Link>
                <Link to="/blog/bookkeeping-compliance-uk" className="related-link">Bookkeeping Compliance</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default ComplianceChecklistUK;